import React, { useState, useRef, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { SizeContext } from 'components/context/SizeProvider';
import { useDismissOnOutside } from 'hooks/useDismissOnOutside';

export const SidebarDropdown = ({
  trigger,
  children,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const { isMobile } = useContext(SizeContext);

  // Calculate position when opening
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      if (isMobile) {
        // On mobile, center the dropdown
        setCoords({
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        });
      } else {
        // Desktop: position to the right
        setCoords({
          bottom: window.innerHeight - rect.bottom,
          left: rect.right + 8,
          transform: 'none'
        });
      }
    }
  }, [isOpen, isMobile]);

  // Positioning above stays bespoke (right-of-trigger on desktop, viewport-centred on
  // mobile); only the dismissal is shared.
  useDismissOnOutside(isOpen, () => setIsOpen(false), [triggerRef, dropdownRef]);

  const dropdown = isOpen && coords && (
    <>
      {/* Backdrop for mobile */}
      {isMobile && (
        <div
          className="fixed inset-0 bg-ui-scrim z-[9998]"
          onClick={() => setIsOpen(false)}
        />
      )}
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          ...(coords.top !== undefined && { top: coords.top }),
          ...(coords.bottom !== undefined && { bottom: coords.bottom }),
          left: coords.left,
          transform: coords.transform,
          zIndex: 9999
        }}
        className={classNames(
          'min-w-56',
          'bg-ui-elevated border border-ui-hair-3 rounded-field shadow-menu',
          {
            'w-[90vw] max-w-sm': isMobile
          }
        )}
      >
        {typeof children === 'function' ? children({ close: () => setIsOpen(false), isOpen, isMobile }) : children}
      </div>
    </>
  );

  return (
    <div className={className}>
      <div ref={triggerRef} onClick={() => setIsOpen(!isOpen)}>
        {trigger}
      </div>
      {createPortal(dropdown, document.body)}
    </div>
  );
};
